/**
 * O que a tela Poder concedente decide sozinha (`PC-01`, ADR-072): só a apresentação.
 *
 * As entregas, os prazos, as cláusulas e a cobertura são do servidor — do catálogo do domínio,
 * `transit_domain.poder_concedente`. Aqui fica a cor de cada cobertura (sempre com o texto ao
 * lado, §6) e as frases que a tela monta com o que veio.
 */
import type {
  AlertaNaTela,
  CanalNaTela,
  ConcessaoNaGestao,
  ConcessaoNoPoderConcedente,
  DiaDeLeituras,
  EntregaNaTela,
  EnvioNaTela,
  FalhaNaGestao,
  IndiceNaGestao,
  IndicesNaGestao,
  ItemDoCalendarioNaTela,
  PerfilNaTela,
} from "../api/tipos";
import type { Significado } from "../componentes/tinta";
import { horaEmBrasilia } from "../formato";
import { agoraDoInquilino } from "../relogio";
import { duracao, percentualDoIndice } from "./indices";

// A hora em Brasília mora no `formato.ts` desde a contingência (`R-06`), que também a usa; daqui
// ela continua saindo para quem já a importava.
export { horaEmBrasilia };

/** A cor de cada cobertura. O `Record` obriga a ter uma para cada valor que a API manda. */
export const COR_DA_COBERTURA: Record<EntregaNaTela["cobertura"], Significado> =
  {
    PRONTA: "verde",
    PARCIAL: "amarelo",
    AINDA_NAO: "vermelho",
    FORA_DO_COCKPIT: "cinza",
  };

/** O dia em Brasília, como a pessoa lê. */
export function diaEmBrasilia(iso: string): string {
  return new Date(iso).toLocaleDateString("pt-BR", {
    timeZone: "America/Sao_Paulo",
  });
}

/** "desde 16/10/2026, por Fulano" — quem definiu pode ter saído da organização. */
export function desdeQuando(concessao: ConcessaoNoPoderConcedente): string {
  if (!concessao.desde) return "";
  const por = concessao.por ? `, por ${concessao.por}` : "";
  return `desde ${diaEmBrasilia(concessao.desde)}${por}`;
}

/** Quantas entregas de cada cobertura o perfil tem — o resumo do topo do cartão. */
export function contarCoberturas(
  perfil: PerfilNaTela,
): Record<EntregaNaTela["cobertura"], number> {
  const contagem: Record<EntregaNaTela["cobertura"], number> = {
    PRONTA: 0,
    PARCIAL: 0,
    AINDA_NAO: 0,
    FORA_DO_COCKPIT: 0,
  };
  for (const grupo of perfil.grupos)
    for (const entrega of grupo.entregas) contagem[entrega.cobertura] += 1;
  return contagem;
}

/** A meta de um índice do contrato, em texto: o mínimo, ou que o limite não foi recebido. */
export function textoDaMeta(minimo: number | null | undefined): string {
  return minimo === null || minimo === undefined
    ? "limite do poder concedente, ainda não recebido"
    : `mínimo de ${minimo.toLocaleString("pt-BR")}%`;
}

/** O prazo do aviso de falha crítica: "2 horas". */
export function textoDoAviso(horas: number | null | undefined): string | null {
  if (horas === null || horas === undefined) return null;
  return `${horas.toLocaleString("pt-BR")} ${horas === 1 ? "hora" : "horas"}`;
}

// --------------------------------------------------------------------------- o calendário (`PC-02`)
//
// O vencimento, a prorrogação, a situação e os dias úteis são do servidor — do domínio,
// `transit_domain.poder_concedente`, sobre o calendário de expediente do regulador. Aqui só a
// cor de cada situação e as frases.

/** A cor de cada situação, sempre com o rótulo ao lado (§6). */
export const COR_DA_SITUACAO: Record<
  ItemDoCalendarioNaTela["situacao"],
  Significado
> = {
  ENTREGUE: "verde",
  ENTREGUE_COM_ATRASO: "amarelo",
  ATRASADA: "vermelho",
  VENCE_HOJE: "amarelo",
  A_VENCER: "cinza",
};

const DIAS_DA_SEMANA = [
  "domingo",
  "segunda-feira",
  "terça-feira",
  "quarta-feira",
  "quinta-feira",
  "sexta-feira",
  "sábado",
];

/**
 * Um dia de calendário (`aaaa-mm-dd`) como a pessoa lê: "20/10/2026". Sem `new Date(iso)`:
 * a data sem hora é meia-noite UTC, que em Brasília ainda é a véspera.
 */
export function dataDoDia(iso: string): string {
  const [ano, mes, dia] = iso.split("-");
  return `${dia}/${mes}/${ano}`;
}

/** O dia da semana de um dia de calendário (`aaaa-mm-dd`). */
export function diaDaSemana(iso: string): string {
  const [ano = 0, mes = 1, dia = 1] = iso.split("-").map(Number);
  return (
    DIAS_DA_SEMANA[new Date(Date.UTC(ano, mes - 1, dia)).getUTCDay()] ?? ""
  );
}

const plural = (n: number, um: string, varios: string) =>
  `${n} ${n === 1 ? um : varios}`;

/** O quando de um item: "em 2 dias úteis", "vence hoje", "há 11 dias", "entregue em …". */
export function quandoDoItem(item: ItemDoCalendarioNaTela): string {
  switch (item.situacao) {
    case "A_VENCER":
      return `em ${plural(item.dias_uteis, "dia útil", "dias úteis")}`;
    case "VENCE_HOJE":
      return "vence hoje";
    case "ATRASADA":
      return `venceu há ${plural(item.dias_de_atraso, "dia", "dias")}`;
    case "ENTREGUE":
      return item.registro
        ? `entregue em ${dataDoDia(item.registro.entregue_em)}`
        : "entregue";
    case "ENTREGUE_COM_ATRASO":
      return item.registro
        ? `entregue em ${dataDoDia(item.registro.entregue_em)}, ${plural(item.dias_de_atraso, "dia", "dias")} depois do vencimento`
        : "entregue com atraso";
  }
}

/** "O contrato dá 20/11/2026 (Zumbi e da Consciência Negra): vai para o dia útil seguinte." */
export function textoDaProrrogacao(
  item: ItemDoCalendarioNaTela,
): string | null {
  if (!item.prorrogado_de) return null;
  const porque = item.motivo_da_prorrogacao
    ? ` (${item.motivo_da_prorrogacao})`
    : "";
  return `O contrato dá ${dataDoDia(item.prorrogado_de)}${porque}, sem expediente: vai para o dia útil seguinte.`;
}

export type ItensAgrupados = {
  atrasadas: ItemDoCalendarioNaTela[];
  aVencer: ItemDoCalendarioNaTela[];
  entregues: ItemDoCalendarioNaTela[];
};

/**
 * Os itens da janela em três listas: o atrasado sem registro primeiro, o que vence (na ordem
 * do vencimento, como o servidor manda) e o entregue, do mais recente para o mais antigo.
 */
export function agruparItens(itens: ItemDoCalendarioNaTela[]): ItensAgrupados {
  const atrasadas = itens.filter((i) => i.situacao === "ATRASADA");
  const aVencer = itens.filter(
    (i) => i.situacao === "VENCE_HOJE" || i.situacao === "A_VENCER",
  );
  const entregues = itens
    .filter(
      (i) => i.situacao === "ENTREGUE" || i.situacao === "ENTREGUE_COM_ATRASO",
    )
    .slice()
    .reverse();
  return { atrasadas, aVencer, entregues };
}

/** A chave de um item da janela: a entrega e a competência. */
export function chaveDoItem(item: ItemDoCalendarioNaTela): string {
  return `${item.entrega}|${item.competencia ?? ""}`;
}

// --------------------------------------------------------------------------- o envio (`PC-04`)
//
// Se a entrega sai pelo cockpit, se o mês fechou, o que o pacote leva e o que impede o envio
// são do servidor — do domínio e do fechamento. Aqui, as frases do envio e do recibo.

/** O menor motivo de um reenvio — o servidor confere (`MENOR_MOTIVO_DO_REENVIO`). */
export const MENOR_MOTIVO_DO_REENVIO = 10;

/** O primeiro dia do mês seguinte ao da competência (`aaaa-mm-01`): quando ela pode sair. */
export function inicioDoMesSeguinte(competencia: string): string {
  const [ano = 0, mes = 1] = competencia.split("-").map(Number);
  const seguinte =
    mes === 12 ? { ano: ano + 1, mes: 1 } : { ano, mes: mes + 1 };
  return `${seguinte.ano}-${String(seguinte.mes).padStart(2, "0")}-01`;
}

/** Dia e hora em Brasília: "17/10/2026 14:03". */
export function quandoEmBrasilia(iso: string): string {
  const instante = new Date(iso);
  const fuso = { timeZone: "America/Sao_Paulo" } as const;
  const dia = instante.toLocaleDateString("pt-BR", fuso);
  const hora = instante.toLocaleTimeString("pt-BR", {
    ...fuso,
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${dia} ${hora}`;
}

/** O tamanho de um arquivo como a pessoa lê: "820 bytes", "12,3 kB", "1,2 MB". */
export function tamanhoDoArquivo(bytes: number): string {
  if (bytes < 1024) return `${bytes.toLocaleString("pt-BR")} bytes`;
  const kb = bytes / 1024;
  if (kb < 1024)
    return `${kb.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} kB`;
  return `${(kb / 1024).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} MB`;
}

/** O começo do SHA-256, para ler e comparar de olho; o inteiro vai no `title`. */
export function shaCurto(sha256: string): string {
  return `${sha256.slice(0, 12)}…`;
}

/** Por onde saiu, como a pessoa lê: o código gravado do canal. */
const NOME_DO_CANAL: Record<string, string> = { EMULADO: "emulador" };

export function nomeDoCanal(canal: string): string {
  return NOME_DO_CANAL[canal] ?? canal;
}

/**
 * Uma tentativa de envio em uma frase: "Recebido pelo emulador em 17/10/2026 14:03 ·
 * protocolo EMU-… · por Fulano", ou "Não deu recibo em …: o motivo".
 */
export function textoDoEnvio(envio: EnvioNaTela): string {
  const por = envio.por ? ` · por ${envio.por}` : "";
  if (envio.situacao === "RECEBIDO") {
    const quando = envio.recebido_em ?? envio.enviado_em;
    const protocolo = envio.protocolo ? ` · protocolo ${envio.protocolo}` : "";
    return `Recebido pelo ${nomeDoCanal(envio.canal)} em ${quandoEmBrasilia(quando)}${protocolo}${por}`;
  }
  const motivo = envio.motivo_da_falha ? `: ${envio.motivo_da_falha}` : "";
  return `Não deu recibo em ${quandoEmBrasilia(envio.enviado_em)}${motivo}${por}`;
}

/** O último recibo de uma competência (os envios vêm do mais recente para o mais antigo). */
export function ultimoRecibo(item: ItemDoCalendarioNaTela): EnvioNaTela | null {
  return item.envios.find((e) => e.situacao === "RECEBIDO") ?? null;
}

/**
 * O que sai pelo cockpit, e por onde, em uma frase — os nomes vêm dos itens da janela que o
 * servidor marcou (`sai_pelo_cockpit`), nunca escritos aqui.
 */
export function textoDoCanal(
  canal: CanalNaTela | null,
  itens: ItemDoCalendarioNaTela[],
): string {
  if (!canal) return "O envio ao regulador ainda não sai do cockpit.";
  const simulado = canal.simulado
    ? " — simulado: nada chega ao regulador de verdade"
    : "";
  const nomes = [
    ...new Set(itens.filter((i) => i.sai_pelo_cockpit).map((i) => i.nome)),
  ];
  if (nomes.length === 0)
    return `Nenhuma entrega da janela sai pelo cockpit (o canal é o ${canal.nome}${simulado}).`;
  const lista =
    nomes.length === 1
      ? nomes[0]
      : `${nomes.slice(0, -1).join(", ")} e ${nomes[nomes.length - 1]}`;
  return `${lista} ${nomes.length === 1 ? "sai" : "saem"} pelo ${canal.nome}${simulado}.`;
}

// --------------------------------------------------------------------------- o regulador (`PC-05`)

/** O que um regulador consultou num dia, em uma frase: "5 consultas · 1 recusada". */
export function textoDasConsultas(dia: DiaDeLeituras): string {
  const consultas = `${dia.consultas.toLocaleString("pt-BR")} ${dia.consultas === 1 ? "consulta" : "consultas"}`;
  if (!dia.recusadas) return consultas;
  return `${consultas} · ${dia.recusadas.toLocaleString("pt-BR")} ${dia.recusadas === 1 ? "recusada" : "recusadas"}`;
}

/** As telas de um dia, com quantas vezes: "a fila da SENATRAN (3), os Indicadores". */
export function textoDasTelas(
  telas: { rotulo: string; quantas: number }[],
): string {
  return telas
    .map((t) => (t.quantas > 1 ? `${t.rotulo} (${t.quantas})` : t.rotulo))
    .join(", ");
}

// --------------------------------------------------------------------------- a gestão à vista (`PC-08`)

/**
 * A cor de cada situação do aviso ao regulador, sempre com o rótulo ao lado (§6): vencido e
 * fora do prazo são vermelho; o que ainda corre — e o da suspeita, que pode ser crítica —,
 * âmbar; no prazo, verde.
 */
export const COR_DO_AVISO: Record<FalhaNaGestao["aviso"], Significado> = {
  SEM_PRAZO: "cinza",
  SE_FOR_CRITICA: "amarelo",
  CORRENDO: "amarelo",
  VENCIDO: "vermelho",
  NO_PRAZO: "verde",
  FORA_DO_PRAZO: "vermelho",
};

/** De quanto em quanto a seção dos índices relê — a mesma cadência da tela dos índices. */
export const RELEITURA_DA_GESTAO_MS = 60_000;

function segundosEntre(de: string | Date, ate: string | Date): number {
  return Math.round((new Date(ate).getTime() - new Date(de).getTime()) / 1000);
}

/**
 * A situação do aviso agora. O servidor a decide quando a tela lê; com a tela aberta, o prazo
 * que corria pode vencer antes da próxima leitura — e o "faltam 0 min" não fica parado.
 */
export function avisoNaHora(
  falha: FalhaNaGestao,
  agora: Date = agoraDoInquilino(),
): { aviso: FalhaNaGestao["aviso"]; rotulo: string } {
  if (
    falha.aviso === "CORRENDO" &&
    falha.avisar_ate &&
    agora.getTime() > new Date(falha.avisar_ate).getTime()
  )
    return { aviso: "VENCIDO", rotulo: "Prazo do aviso vencido" };
  return { aviso: falha.aviso, rotulo: falha.aviso_rotulo };
}

/**
 * O relógio do aviso numa frase: quanto falta, desde quando venceu, ou quando foi avisado e
 * se foi a tempo. O prazo e a situação são do servidor; aqui só a conta do "faltam" com o
 * relógio da organização.
 */
export function relogioDoAviso(
  falha: FalhaNaGestao,
  agora: Date = agoraDoInquilino(),
): string {
  const prazo = falha.avisar_ate;
  const { aviso } = avisoNaHora(falha, agora);
  if (aviso === "SEM_PRAZO" || !prazo) {
    return falha.avisado_em
      ? `Avisado às ${horaEmBrasilia(falha.avisado_em, agora)} — o regime não fixa prazo`
      : "O regime da concessão não fixa prazo para o aviso";
  }
  const as = horaEmBrasilia(prazo, agora);
  const faltam = segundosEntre(agora, prazo);
  // A suspeita ainda não é falha crítica: o relógio corre, mas o aviso é condicional.
  if (aviso === "SE_FOR_CRITICA") {
    return faltam > 0
      ? `Se for falha crítica, avisar o regulador até ${as} — faltam ${duracao(faltam)}`
      : `Se for falha crítica, o prazo do aviso venceu às ${as}`;
  }
  if (aviso === "CORRENDO") {
    return `Avisar o regulador até ${as} — faltam ${duracao(Math.max(0, faltam))}`;
  }
  if (aviso === "VENCIDO") {
    return `O prazo do aviso venceu às ${as}, há ${duracao(Math.max(0, -faltam))}, e o aviso não foi registrado`;
  }
  const avisado = horaEmBrasilia(falha.avisado_em!, agora);
  if (aviso === "NO_PRAZO")
    return `Avisado às ${avisado}, no prazo (até ${as})`;
  return `Avisado às ${avisado}, ${duracao(segundosEntre(prazo, falha.avisado_em!))} depois do prazo (${as})`;
}

/**
 * A resposta tem a forma dos índices na gestão? Uma versão do servidor de antes da `PC-08`
 * (ou outra resposta qualquer) não derruba a tela: a seção só não aparece.
 */
export function eGestao(x: unknown): x is IndicesNaGestao {
  const g = x as IndicesNaGestao | null;
  return (
    !!g &&
    typeof g.periodo?.rotulo === "string" &&
    Array.isArray(g.concessoes) &&
    g.concessoes.every(
      (c) => Array.isArray(c.indices) && Array.isArray(c.falhas),
    )
  );
}

/** A falha que pede alguém agora: a crítica sem aviso, e a suspeita em aberto. */
export function pedeAtencao(
  falha: FalhaNaGestao,
  agora: Date = agoraDoInquilino(),
): boolean {
  const { aviso } = avisoNaHora(falha, agora);
  if (aviso === "CORRENDO" || aviso === "VENCIDO") return true;
  return falha.situacao === "SUSPEITA" && falha.fim == null;
}

/** As frases do topo da tela, o vencido primeiro. */
export function avisosDasFalhas(
  concessoes: ConcessaoNaGestao[],
  agora: Date = agoraDoInquilino(),
): { criticos: string[]; atencao: string[] } {
  const criticos: string[] = [];
  const atencao: string[] = [];
  const ordem: Partial<Record<FalhaNaGestao["aviso"], number>> = {
    VENCIDO: 0,
    CORRENDO: 1,
  };
  const todas = concessoes.flatMap((c) =>
    c.falhas
      .filter((f) => pedeAtencao(f, agora))
      .map((f) => ({ c, f, aviso: avisoNaHora(f, agora).aviso })),
  );
  todas.sort((a, b) => (ordem[a.aviso] ?? 2) - (ordem[b.aviso] ?? 2));
  for (const { c, f, aviso } of todas) {
    const onde = `${f.praca} (${c.nome})`;
    if (aviso === "CORRENDO" || aviso === "VENCIDO") {
      criticos.push(
        `${onde}: falha crítica desde ${horaEmBrasilia(f.inicio, agora)}. ${relogioDoAviso(f, agora)}.`,
      );
    } else {
      const prazo = f.avisar_ate ? ` ${relogioDoAviso(f, agora)}.` : "";
      atencao.push(
        `${onde} parado desde ${horaEmBrasilia(f.inicio, agora)}, à espera da supervisão.${prazo}`,
      );
    }
  }
  return { criticos, atencao };
}

/** "Abaixo da meta desde 12/10" — o dia vem do servidor. */
export function textoDoAbaixo(indice: IndiceNaGestao): string | null {
  if (!indice.abaixo_desde) return null;
  const [, mes, dia] = indice.abaixo_desde.split("-");
  return `Abaixo da meta desde ${dia}/${mes}`;
}

function diaCurto(dia: string): string {
  const [, mes, d] = dia.split("-");
  return `${d}/${mes}`;
}

/** O que a linha da tendência mostra, em texto — para o leitor de tela e o `title`. */
export function descricaoDaTendencia(indice: IndiceNaGestao): string {
  const pontos = indice.tendencia.filter((p) => p.percentual != null);
  if (pontos.length === 0)
    return `${indice.rotulo}: nada contado no mês ainda.`;
  const primeiro = pontos[0]!;
  const ultimo = pontos[pontos.length - 1]!;
  const meta =
    indice.meta?.minimo != null
      ? `; ${indice.meta.referencia ? "referência federal" : "meta"} ${percentualDoIndice(String(indice.meta.minimo))}`
      : "";
  return (
    `${indice.rotulo}, acumulado do mês: ${percentualDoIndice(String(primeiro.percentual))} em ` +
    `${diaCurto(primeiro.dia)} e ${percentualDoIndice(String(ultimo.percentual))} em ${diaCurto(ultimo.dia)}${meta}.`
  );
}

/**
 * O que o cockpit publicou desta falha, para quem assina "Pórtico parado" no WhatsApp (e o
 * monitoramento). A entrega no celular é do WhatsBI, que não responde a este módulo: a frase
 * diz o que saiu, não o que chegou.
 */
export function textoDosAlertas(
  alertas: AlertaNaTela[],
  agora: Date = agoraDoInquilino(),
): string | null {
  if (alertas.length === 0) return null;
  const partes = alertas.map(
    (a) =>
      `${a.rotulo.toLowerCase()} às ${horaEmBrasilia(a.alertado_em, agora)}`,
  );
  return `Alerta do cockpit, no WhatsApp de quem assina "Pórtico parado": ${partes.join("; ")}.`;
}
